import React from 'react';
import AMPageShell from './components/AMPageShell';
import Icon from 'components/AppIcon';
import { ASSETS } from './data/assetData';
import { useNavigate } from 'react-router-dom';

const RISK_COLOUR = (score) => {
  if (score <= 2) return { bg: 'bg-success/10', text: 'text-success', label: 'Low' };
  if (score === 3) return { bg: 'bg-warning/10', text: 'text-warning', label: 'Medium' };
  return { bg: 'bg-error/10', text: 'text-error', label: 'High' };
};

const TREND_ICON = (trend) => {
  const last = trend?.[trend?.length - 1];
  const prev = trend?.[trend?.length - 2];
  if (last > prev) return { icon: 'TrendingUp', colour: 'var(--color-error)', label: 'Deteriorating' };
  if (last < prev) return { icon: 'TrendingDown', colour: 'var(--color-success)', label: 'Improving' };
  return { icon: 'Minus', colour: 'var(--color-muted-foreground)', label: 'Stable' };
};

export default function RiskMonitor({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const navigate = useNavigate();
  return (
    <AMPageShell activeTab="/asset-management/risk-monitor">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {assets?.map((asset) => {
            const riskC = RISK_COLOUR(asset?.riskScore);
            const cpC = RISK_COLOUR(asset?.counterpartyRiskScore);
            const trend = TREND_ICON(asset?.riskTrend);
            return (
              <div key={asset?.id} className="bg-card border border-border rounded-lg shadow-xs p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <button
                      onClick={() => navigate(`/portfolio/assets/${asset?.id}`, { state: { from: 'asset-management', backPath: '/asset-management/risk', backLabel: 'Risk Monitor' } })}
                      className="font-heading font-semibold text-primary hover:underline text-left" style={{ fontSize: '1rem' }}
                    >
                      {asset?.name}
                    </button>
                    <p className="font-caption text-xs text-muted-foreground mt-0.5">{asset?.sector} · {asset?.country}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Icon name={trend?.icon} size={14} color={trend?.colour} strokeWidth={2.5} />
                    <span className="font-caption text-xs" style={{ color: trend?.colour }}>{trend?.label}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className={`rounded-md p-3 ${riskC?.bg}`}>
                    <p className="font-caption text-xs text-muted-foreground">Risk Score</p>
                    <p className={`font-data text-xl font-semibold ${riskC?.text}`}>{asset?.riskScore}/5</p>
                    <p className={`font-caption text-xs ${riskC?.text}`}>{riskC?.label}</p>
                  </div>
                  <div className={`rounded-md p-3 ${cpC?.bg}`}>
                    <p className="font-caption text-xs text-muted-foreground">Counterparty Risk</p>
                    <p className={`font-data text-xl font-semibold ${cpC?.text}`}>{asset?.counterpartyRiskScore}/5</p>
                    <p className={`font-caption text-xs ${cpC?.text}`}>{cpC?.label}</p>
                  </div>
                </div>
                <div className="space-y-1.5 mb-3">
                  <div className="flex justify-between text-xs font-caption">
                    <span className="text-muted-foreground">Disputes</span>
                    <span className={`font-medium ${
                      asset?.disputesStatus === 'None' ? 'text-success' :
                      asset?.disputesStatus === 'Minor' ? 'text-warning' : 'text-error'
                    }`}>{asset?.disputesStatus}</span>
                  </div>
                  <div className="flex justify-between text-xs font-caption">
                    <span className="text-muted-foreground">Stakeholder Relations</span>
                    <span className={`font-medium ${
                      asset?.stakeholderStatus === 'Strong' ? 'text-success' :
                      asset?.stakeholderStatus === 'Satisfactory' ? 'text-foreground' : 'text-error'
                    }`}>{asset?.stakeholderStatus}</span>
                  </div>
                </div>
                <div className="bg-muted/30 rounded-md p-3">
                  <p className="font-caption text-xs text-muted-foreground mb-1">Situation Update</p>
                  <p className="font-body text-xs text-foreground leading-relaxed">{asset?.situationUpdate}</p>
                </div>
                {/* Risk trend dots */}
                <div className="flex items-center gap-2 mt-3">
                  <span className="font-caption text-xs text-muted-foreground">Last 3 reviews:</span>
                  {asset?.riskTrend?.map((score, i) => {
                    const c = RISK_COLOUR(score);
                    return (<span key={i} className={`w-5 h-5 rounded-full flex items-center justify-center font-data text-xs font-semibold ${c?.bg} ${c?.text}`}>{score}</span>);
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AMPageShell>
  );
}
